import { computed, inject, Injectable } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { ReportsService } from '../../Services/reports.service';
import { GetAnomaliesReport } from '../../Interfaces/api/reports.interface';
import { AtRiskDeviceEntry, ChartDatum } from '../../Modules/Reports/models/report-dashboard.types';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';

export interface AnomalyBucketStat {
  key: string;
  count: number;
  percent: number | null;
}

type WeightedBucketKey =
  | 'notEncrypted'
  | 'noncompliant'
  | 'noAutopilotHash'
  | 'disabledUsers'
  | 'inactiveDevices'
  | 'multipleUsers';

const PENALTY_CAP = 6;

const WEIGHTED_BUCKETS: readonly { key: WeightedBucketKey; weight: number }[] = [
  { key: 'notEncrypted', weight: 3 },
  { key: 'noncompliant', weight: 3 },
  { key: 'noAutopilotHash', weight: 2 },
  { key: 'disabledUsers', weight: 2 },
  { key: 'inactiveDevices', weight: 1 },
  { key: 'multipleUsers', weight: 1 },
];

@Injectable()
export class AnomaliesReportFacade {
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _reportsService = inject(ReportsService);

  private readonly _resource = httpResource<GetAnomaliesReport>(() =>
    this._reportsService.anomalies()
  );

  readonly isLoading = computed(() => this._resource.isLoading());
  readonly error = computed(() => this._resource.error());
  readonly data = computed(() => this._resourceState.valueOf(this._resource)?.data ?? null);

  readonly appFailure = computed(() => this.data()?.appFailure ?? []);
  readonly multipleUsers = computed(() => this.data()?.multipleUsers ?? []);
  readonly notEncrypted = computed(() => this.data()?.notEncrypted ?? []);
  readonly noAutopilotHash = computed(() => this.data()?.noAutopilotHash ?? []);
  readonly inactiveDevices = computed(() => this.data()?.inactiveDevices ?? []);
  readonly noncompliant = computed(() => this.data()?.noncompliant ?? []);
  readonly osEdition = computed(() => this.data()?.osEdition ?? []);
  readonly disabledUsers = computed(() => this.data()?.disabledUsers ?? []);

  readonly deviceUniverse = computed(() => this.osEdition());
  readonly universeCount = computed(() => this.deviceUniverse().length);

  readonly bucketStats = computed<AnomalyBucketStat[]>(() => this.buildBucketStats());
  readonly penaltyByDevice = computed<Map<string, AtRiskDeviceEntry>>(() => this.buildPenaltyMap());
  readonly healthScore = computed<number | null>(() => this.computeHealthScore());
  readonly atRiskDevices = computed<AtRiskDeviceEntry[]>(() => this.rankAtRiskDevices());
  readonly topAtRiskDevices = computed<AtRiskDeviceEntry[]>(() => this.atRiskDevices().slice(0, 10));

  readonly complianceDonutData = computed<ChartDatum[]>(() => this.buildComplianceDonutData());
  readonly categoryBarData = computed<ChartDatum[]>(() =>
    this.bucketStats().map(stat => ({ name: stat.key, value: stat.count, key: stat.key }))
  );
  readonly osEditionDistribution = computed<ChartDatum[]>(() => this.buildOsEditionDistribution());
  readonly topFailingApps = computed<ChartDatum[]>(() => this.buildTopFailingApps());

  reload(): void {
    this._resource.reload();
  }

  private buildBucketStats(): AnomalyBucketStat[] {
    const n = this.universeCount();
    const counts: readonly [string, number][] = [
      ['notEncrypted', this.notEncrypted().length],
      ['noncompliant', this.noncompliant().length],
      ['noAutopilotHash', this.noAutopilotHash().length],
      ['disabledUsers', this.disabledUsers().length],
      ['inactiveDevices', this.inactiveDevices().length],
      ['multipleUsers', this.multipleUsers().length],
      ['appFailure', this.appFailure().length],
    ];
    return counts.map(([key, count]) => ({ key, count, percent: n > 0 ? count / n : null }));
  }

  private bucketDevices(key: WeightedBucketKey): readonly { deviceName: string }[] {
    switch (key) {
      case 'notEncrypted':
        return this.notEncrypted();
      case 'noncompliant':
        return this.noncompliant();
      case 'noAutopilotHash':
        return this.noAutopilotHash();
      case 'disabledUsers':
        return this.disabledUsers();
      case 'inactiveDevices':
        return this.inactiveDevices();
      case 'multipleUsers':
        return this.multipleUsers();
      default:
        return [];
    }
  }

  private buildPenaltyMap(): Map<string, AtRiskDeviceEntry> {
    const map = new Map<string, AtRiskDeviceEntry>();
    for (const bucket of WEIGHTED_BUCKETS) {
      for (const device of this.bucketDevices(bucket.key)) {
        this.applyPenalty(map, device.deviceName, bucket.key, bucket.weight);
      }
    }
    return map;
  }

  private applyPenalty(map: Map<string, AtRiskDeviceEntry>, deviceName: string, bucketKey: string, weight: number): void {
    if (!deviceName) return;
    const existing = map.get(deviceName);
    const entry: AtRiskDeviceEntry = existing
      ? {
          deviceName,
          penalty: Math.min(PENALTY_CAP, existing.penalty + weight),
          memberships: [...existing.memberships, bucketKey],
        }
      : { deviceName, penalty: Math.min(PENALTY_CAP, weight), memberships: [bucketKey] };
    map.set(deviceName, entry);
  }

  private computeHealthScore(): number | null {
    const n = this.universeCount();
    if (n === 0) return null;
    const penalties = this.penaltyByDevice();
    const sum = this.deviceUniverse().reduce((acc, device) => {
      const penalty = penalties.get(device.deviceName)?.penalty ?? 0;
      return acc + (1 - penalty / PENALTY_CAP);
    }, 0);
    return Math.round((100 * sum) / n);
  }

  private rankAtRiskDevices(): AtRiskDeviceEntry[] {
    return Array.from(this.penaltyByDevice().values()).sort(
      (a, b) => b.penalty - a.penalty || a.deviceName.localeCompare(b.deviceName)
    );
  }

  private buildComplianceDonutData(): ChartDatum[] {
    const n = this.universeCount();
    const noncompliantCount = this.noncompliant().length;
    return [
      { name: 'compliant', value: Math.max(0, n - noncompliantCount) },
      { name: 'noncompliant', value: noncompliantCount, key: 'noncompliant' },
    ];
  }

  private buildOsEditionDistribution(): ChartDatum[] {
    const groups = new Map<string, number>();
    for (const device of this.osEdition()) {
      const edition = device.operatingSystemEdition;
      groups.set(edition, (groups.get(edition) ?? 0) + 1);
    }
    return Array.from(groups.entries()).map(([name, value]) => ({ name, value, key: 'osEdition' }));
  }

  private buildTopFailingApps(): ChartDatum[] {
    return [...this.appFailure()]
      .sort((a, b) => Number(b.FailedDeviceCount) - Number(a.FailedDeviceCount))
      .slice(0, 10)
      .map(app => ({ name: app.DisplayName, value: Number(app.FailedDeviceCount), key: 'appFailure' }));
  }
}
